// Shapes of the Partner API (src/EPN.Identity.Api/Features/Partners). Enums travel as their names. Nothing here is a permission decision: the server authorizes every call.

export type PartnerRole = 'Owner' | 'Admin' | 'PayrollManager' | 'PayrollProcessor' | 'ReadOnly';

export type PartnerStatus =
  | 'ApplicationDraft'
  | 'Submitted'
  | 'UnderReview'
  | 'InformationRequired'
  | 'Approved'
  | 'Active'
  | 'Suspended'
  | 'Terminated'
  | 'Rejected';

export type RelationshipStatus =
  | 'Prospect'
  | 'InvitationSent'
  | 'AwaitingClientAcceptance'
  | 'Onboarding'
  | 'Active'
  | 'Suspended'
  | 'Revoked'
  | 'Ended';

export interface PartnerMembershipSummary {
  partnerOrganizationId: string;
  legalName: string;
  status: PartnerStatus;
  role: PartnerRole;
  /** Display only: the server decides every request. */
  permissions: string[];
}

export interface PartnerMeResponse {
  memberships: PartnerMembershipSummary[];
  /** Whether this session already proves authenticator-app MFA. Every partner route needs it. */
  mfaCompleted: boolean;
}

export interface PartnerOrganization {
  id: string;
  legalName: string;
  dba: string | null;
  firmType: string;
  einLast4: string | null;
  primaryContactName: string | null;
  primaryContactEmail: string | null;
  primaryContactPhone: string | null;
  supportEmail: string | null;
  website: string | null;
  statesServed: string[];
  verificationStatus: string;
  status: PartnerStatus;
  statusReason: string | null;
  agreementVersion: string | null;
  agreementAcceptedAtUtc: string | null;
  createdAtUtc: string;
  approvedAtUtc: string | null;
  activatedAtUtc: string | null;
}

export interface PartnerClient {
  id: string;
  clientName: string;
  clientCompanyName: string | null;
  invitedEmail: string;
  status: RelationshipStatus;
  requestedServices: string[];
  grantedServices: string[];
  accessExpiresAtUtc: string | null;
  invitedAtUtc: string;
  invitationExpiresAtUtc: string | null;
  acceptedAtUtc: string | null;
  activatedAtUtc: string | null;
  revokedAtUtc: string | null;
  endedAtUtc: string | null;
  lastReviewedAtUtc: string | null;
  assignedMemberCount: number;
}

export interface InviteClientRequest {
  clientName: string;
  invitedEmail: string;
  services: string[];
}

/** `invitationToken` is shown once. The server stores only its hash and never returns it again. */
export interface PartnerClientInvitationResult {
  client: PartnerClient;
  invitationToken: string;
}

export interface PartnerTeamMember {
  membershipId: string;
  userId: string;
  email: string;
  displayName: string | null;
  role: PartnerRole;
  isActive: boolean;
  createdAtUtc: string;
}

export interface PartnerAssignment {
  assignmentId: string;
  membershipId: string;
  email: string;
  displayName: string | null;
  role: PartnerRole;
  assignedAtUtc: string;
}

export interface PartnerHistoryEntry {
  occurredAtUtc: string;
  eventType: string;
  actorEmail: string | null;
  outcome: string | null;
  reason: string | null;
  detailsJson: string | null;
}

/** What the page may show after entering a client. The delegated token itself goes only into an httpOnly cookie and is never in this response. */
export interface EnterClientSummary {
  relationshipId: string;
  clientName: string;
  partnerRole: PartnerRole;
  scopes: string[];
  expiresAtUtc: string;
}

// ---- the client company owner's side ----

export interface ServiceOption {
  code: string;
  label: string;
}

export interface InvitationPreview {
  partnerLegalName: string;
  partnerDba: string | null;
  clientName: string;
  requestedServices: ServiceOption[];
  invitationExpiresAtUtc: string | null;
}

export interface AuthorizedPartner {
  id: string;
  partnerLegalName: string;
  partnerDba: string | null;
  status: RelationshipStatus;
  grantedServices: ServiceOption[];
  requestedServices: ServiceOption[];
  accessExpiresAtUtc: string | null;
  acceptedAtUtc: string | null;
  activatedAtUtc: string | null;
  suspendedAtUtc: string | null;
  suspensionReason: string | null;
  revokedAtUtc: string | null;
  revocationReason: string | null;
  lastReviewedAtUtc: string | null;
  assignedMembers: { email: string; displayName: string | null; role: PartnerRole }[];
}

/** The eleven services a firm can ask for, in display order. Codes are the server's; labels are display text. */
export const PARTNER_SERVICES: ServiceOption[] = [
  { code: 'company.setup', label: 'Company setup' },
  { code: 'employees.setup', label: 'Employee setup' },
  { code: 'history.import', label: 'Historical payroll import' },
  { code: 'payroll.prepare', label: 'Payroll preparation' },
  { code: 'payroll.review', label: 'Payroll review' },
  { code: 'reports.payroll', label: 'Payroll reports' },
  { code: 'tax.payments.support', label: 'Tax-payment support' },
  { code: 'tax.filings.support', label: 'Tax-filing support' },
  { code: 'accounting.export', label: 'Accounting export' },
  { code: 'benefits.setup', label: 'Benefits and deductions setup' },
  { code: 'other', label: 'Other authorized service' },
];

export const PARTNER_ROLE_LABELS: Record<PartnerRole, string> = {
  Owner: 'Partner Owner',
  Admin: 'Partner Admin',
  PayrollManager: 'Payroll Manager',
  PayrollProcessor: 'Payroll Processor',
  ReadOnly: 'Read Only',
};

export const RELATIONSHIP_STATUS_LABELS: Record<RelationshipStatus, string> = {
  Prospect: 'Prospect',
  InvitationSent: 'Invitation sent',
  AwaitingClientAcceptance: 'Awaiting client acceptance',
  Onboarding: 'Onboarding',
  Active: 'Active',
  Suspended: 'Suspended',
  Revoked: 'Revoked',
  Ended: 'Ended',
};
